// Moved from `src/features/workspace/hooks/useQueryWorkspaces.ts` (now a
// shim) at AW1, ARCFE-984. Import re-points only: the token crosses the seam
// as the reactive hook (S2), the module-scope invalidator runs on the seam
// queryClient, and the 400-settles-immediately retry keeps its exact
// semantics via `isTransportError` — the structural view — so the package
// never names axios (the surface rule, applied to internals too for one
// consistent idiom).
import { skipToken, useQuery } from '@tanstack/react-query';
import { GET_WORKSPACES, type ListWorkspacesResponse } from '../../contracts';
import { listWorkspaces } from '../api/workspaces';
import { getWorkspaceDataConfig, isTransportError } from '../config';

export { GET_WORKSPACES };

export const invalidateWorkspaces = () =>
  getWorkspaceDataConfig().queryClient.invalidateQueries({
    queryKey: [GET_WORKSPACES],
  });

/**
 * Lists the authenticated user's workspaces. Drives `workspaceIdAtom`
 * hydration via the app-side `useHydrateWorkspaceId` (MUL-1991 — ruled app
 * composition at P0.5); future workspace switcher UI (M8) will share the
 * same cache.
 */
export function useQueryWorkspaces() {
  const token = getWorkspaceDataConfig().useAuthToken();

  return useQuery<ListWorkspacesResponse>({
    queryKey: [GET_WORKSPACES],
    queryFn: token ? () => listWorkspaces() : skipToken,
    staleTime: 5 * 60 * 1000,
    // A 400 means the DB user isn't provisioned yet (new-signup window); it
    // won't self-heal by retrying, so settle immediately and let the hydration
    // gate fail open. Genuine transient failures (5xx / network) still retry.
    // 401 deliberately keeps retrying: a transiently-failed force-refresh
    // rejects the original 401 with the session still alive, so the request
    // can recover — the gate classifies expiry from auth state, not the status.
    retry: (failureCount, error: unknown) => {
      if (isTransportError(error) && error.response?.status === 400) {
        return false;
      }
      return failureCount < 3;
    },
  });
}
